import { FormSelect } from "../../../components/ui/select";
import {
  ArrowLeft,
  Check,
  Clock3,
  FileText,
  Lock,
  Save,
  Send,
  Sparkles,
  XCircle,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { fetchComplaintDetail } from "../../../services/fsmService";
import {
  downloadQuotationPdf,
  getQuotationById,
  extractQuotationsList,
  fetchSalesExecutiveOptions,
  fetchSalesSupportOptions,
  getQuotations,
  mapQuotationTypeForApi,
  retryImportQuotation,
  reviseQuotation,
  submitQuotation,
  updateQuotation,
  updateQuotationStatus,
  updateQuotationWorkingSheet,
} from "../../../services/quotationService";
import { updateTerm } from "../../../services/salesConfigService";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import {
  QuotationMobileCustomerCard,
  QuotationMobileFooterBar,
  QuotationMobileMenuSheet,
  QuotationMobileHeader,
  QuotationMobileSummaryPanel,
} from "../mobile-pages/QuotationMobileShell";
import {
  CUSTOMER_STAGE_STATUSES,
  sq,
  localizedFieldProps,
  translateQuotationType,
  supportsInstallation,
  translateQuotationStatus,
  fetchEmployeeOptions,
  seedPmcEntriesFromQuotation,
  seedTermsStateFromQuotation,
  hasQuotationPmcData,
  hasQuotationSpecificTermsData,
  normalizeSpecificTermsCollection,
  loadPmcEntriesForQuotation,
  loadSpecificTermsForQuotation,
  loadTermsTabState,
  formatCurrency,
  hasBackendPricingMetrics,
  normalizeStatusLabel,
  mapQuotationStatusForApi,
  resolveBackendVersionNo,
  resolveLatestBackendVersionNo,
  markQuotationVersionCreated,
  hasQuotationCreatedVersion,
  syncCreatedVersionLocksFromHistory,
  isLatestQuotationVersion,
  buildQuotationVersionHistory,
  extractApiCollection,
  extractNumericId,
  parseContentDispositionFilename,
  triggerBrowserDownload,
  extractObjectPayload,
  unwrapQuotationDetailPayload,
  hasQuotationDetailPayload,
  quotationIsEditable,
  isPmcCostHeadActive,
  buildTermApiPayload,
  filterTermsForQuotationType,
  loadStandardTermsForQuotationType,
  resolvePmcEntriesForQuotation,
  normalizeApiQuotation,
  buildWorkingSheetPayload,
  isClientOnlyBlankWorkingRow,
  filterOutClientOnlyBlankWorkingRows,
  dedupeWorkingRowsByLineItemId,
  reloadMobileWorkingRowsAfterSave,
  ensureWorkingRowsLoadedForSave,
  isClientPlaceholderSystemName,
  isClientPlaceholderSystemId,
  rowBelongsToSystem,
  resolveQuotationSystems,
  ensureSystemWorkingRows,
  validateWorkingRowsForSubmit,
  hydrateQuotation,
  normalizeAdditionalTermEntry,
  buildSpecificTermsPayload,
  resolveTermsFromQuotation,
  toExcelWholeRate,
  computePreparedWorkingRow,
  applyNetPriceRuleToRows,
  computeWorkingTotals,
  pickQuotationServiceRoleFields,
  mergeQuotationServiceRoleFields,
  toAssignedEmployeeId,
  resolveAssignedEmployeeId,
  resolveEmployeeDisplayName,
  filterControlClass,
  CLEARED_BACKEND_AMOUNT_FIELDS,
  resolveCostPricingMultiplier,
  computeWorkingSheetPricingMetrics,
  multipliersApproximatelyEqual,
  multiplierValueFollowsCostPricing,
  formatMultiplierFieldValue,
} from "./quotationUtils";
import {
  StatusPill,
  SectionShell,
  QuotationPrimaryButton,
  QuotationSecondaryButton,
  ErrorState,
  VersionHistoryDrawer,
  QuotationConfirmModal,
  QuotationImportFailureDialog,
} from "./QuotationComponents";
import { WorkingSheetTab } from "./QuotationWorkingSheetTab";
import { CostPricingTab } from "./QuotationCostPricingTab";
import { TermsAndConditionsTab } from "./QuotationTermsTab";
import { useLazyQuotationVersionHistory, useQuotationAutoRefresh } from "./quotationHooks";


function QuotationWorkspace({ baseQuotation, baseHistory }) {
  useTranslation();
  const navigate = useNavigate();
  const isMobileWorkspace = useIsMobileLayout();
  const [activeTab, setActiveTab] = useState("workingSheet");
  const [quotation, setQuotation] = useState(() => {
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    return {
      ...baseQuotation,
      systems,
      systemNames: systems.map((system) => system.name),
      systemName: systems.map((system) => system.name).join(", "),
    };
  });
  const [workingRows, setWorkingRows] = useState(() => {
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    return systems.length
      ? []
      : ensureSystemWorkingRows(baseQuotation.workingRows, systems, baseQuotation.type);
  });
  const [pmcEntries, setPmcEntries] = useState(() => seedPmcEntriesFromQuotation(baseQuotation.pmcs || []));
  const [hasPmcConfigs, setHasPmcConfigs] = useState(false);
  const [additionalTermEntries, setAdditionalTermEntries] = useState([]);
  const [netPricePercentage, setNetPricePercentage] = useState(0);
  const [standardTerms, setStandardTerms] = useState([]);
  const [selectedStandardTerms, setSelectedStandardTerms] = useState([]);
  const [termsPreviewOrder, setTermsPreviewOrder] = useState([]);
  const flushAdditionalTermsRef = useRef(null);
  const [overheadsPercent, setOverheadsPercent] = useState(String(baseQuotation.overheadsPct ?? "0"));
  const [markUpPercent, setMarkUpPercent] = useState(String(baseQuotation.markUpPct ?? "0"));
  const [preferBackendPricing, setPreferBackendPricing] = useState(true);
  const [statusNote, setStatusNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState(baseHistory);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [complaintBackBusy, setComplaintBackBusy] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [salesExecutiveOptions, setSalesExecutiveOptions] = useState([]);
  const [salesSupportOptions, setSalesSupportOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingSalesExecutives, setLoadingSalesExecutives] = useState(false);
  const [loadingSalesSupport, setLoadingSalesSupport] = useState(false);
  const employeesRequestedRef = useRef(false);
  const salesExecutivesRequestedRef = useRef(false);
  const salesSupportRequestedRef = useRef(false);
  const pmcConfigsLoadedRef = useRef(false);
  const termsLibraryLoadedRef = useRef(false);
  const serviceRoleEnrichmentKeyRef = useRef("");
  const [showVersionConfirm, setShowVersionConfirm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCustomerOpen, setMobileCustomerOpen] = useState(false);
  const [retryExcelFile, setRetryExcelFile] = useState(null);
  const [retryExcelError, setRetryExcelError] = useState("");
  const retryExcelInputRef = useRef(null);
  const creatingVersionRef = useRef(false);
  const handleVersionsLoaded = useCallback((history) => {
    syncCreatedVersionLocksFromHistory(history);
    setVersions(history);
  }, []);
  const { loading: historyLoading, error: historyError } = useLazyQuotationVersionHistory({
    open: historyOpen,
    refId: quotation.refId || baseQuotation.refId,
    fallbackHistory: versions,
    onLoaded: handleVersionsLoaded,
    prefetch: true,
  });
  const latestVersionNo = resolveLatestBackendVersionNo(versions);
  const isLatestVersion = isLatestQuotationVersion(quotation, versions);
  const editable = quotationIsEditable(quotation?.status) && isLatestVersion;
  const isImportFailed =
    String(quotation?.status || "").trim().toLowerCase() === "import failed";
  const [importFailureOpen, setImportFailureOpen] = useState(isImportFailed);

  useEffect(() => {
    setImportFailureOpen(isImportFailed);
  }, [isImportFailed, quotation.id]);

  useQuotationAutoRefresh(quotation);

  const canSaveCustomerDetails = (editable || isImportFailed) && isLatestVersion;
  const isServiceQuotation = String(quotation?.type || "").toUpperCase() === "SERVICE";
  const versionAlreadyCreatedFromThisId = hasQuotationCreatedVersion(quotation.id);
  const workspaceSystems = resolveQuotationSystems(quotation, quotation.workingRows || []);
  // Lazy system lines are only present after expand/load — treat missing systems as unloaded.
  const systemsFullyLoaded =
    workspaceSystems.length === 0 ||
    workspaceSystems.every((system) =>
      workingRows.some((row) => rowBelongsToSystem(row, system))
    );
  const costPricingMultiplier = useMemo(
    () =>
      resolveCostPricingMultiplier({
        rows: workingRows,
        quotationType: quotation.type,
        pmcEntries,
        overheadsPercent,
        markUpPercent,
        backendPricingMetrics: quotation.pricingMetrics,
        // Same cost basis as CostPricingTab so Mult/Install Mult update immediately.
        preferBackendPricing,
        forceBackendSheetCosts: !systemsFullyLoaded,
      }),
    [
      workingRows,
      quotation.type,
      quotation.pricingMetrics,
      pmcEntries,
      overheadsPercent,
      markUpPercent,
      preferBackendPricing,
      systemsFullyLoaded,
    ]
  );

  useEffect(() => {
    if (!editable || !costPricingMultiplier) return;

    setWorkingRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        const isDiscount = String(row.adjustmentType || "Multiplier") === "Discount %";
        const currentRate = formatMultiplierFieldValue(row.adjustmentRate ?? row.multiplier);
        const currentInstall = formatMultiplierFieldValue(row.installationMultiplier);
        const cpMultiplierChanged = !multipliersApproximatelyEqual(row.cpMultiplier, costPricingMultiplier);
        // Refill only multipliers that are still following C+P. Saved edits stay as saved.
        const needsSupplyUpdate =
          !isDiscount &&
          multiplierValueFollowsCostPricing(currentRate, row.cpMultiplier, {
            isManual: Boolean(row.adjustmentRateManual),
            nextDerived: costPricingMultiplier,
          }) &&
          !multipliersApproximatelyEqual(currentRate, costPricingMultiplier);
        const needsInstallUpdate =
          supportsInstallation(quotation.type) &&
          multiplierValueFollowsCostPricing(currentInstall, row.cpMultiplier, {
            isManual: Boolean(row.installationMultiplierManual),
            nextDerived: costPricingMultiplier,
          }) &&
          !multipliersApproximatelyEqual(currentInstall || 0, costPricingMultiplier);

        if (!needsSupplyUpdate && !needsInstallUpdate && !cpMultiplierChanged) return row;
        changed = true;
        return {
          ...row,
          cpMultiplier: costPricingMultiplier,
          ...(needsSupplyUpdate
            ? {
                adjustmentType: "Multiplier",
                adjustmentRate: costPricingMultiplier,
                adjustmentRateManual: false,
                multiplier: costPricingMultiplier,
                // Recalculate Selling Supply from Input × Derived Mult unless user edited unit rate.
                ...(row.unitRateManual
                  ? {}
                  : { unitRate: "", unitRateManual: false }),
                ...CLEARED_BACKEND_AMOUNT_FIELDS,
              }
            : isDiscount
              ? { multiplier: costPricingMultiplier }
              : {}),
          ...(needsInstallUpdate
            ? {
                installationMultiplier: costPricingMultiplier,
                installationMultiplierManual: false,
                ...CLEARED_BACKEND_AMOUNT_FIELDS,
              }
            : {}),
        };
      });
      return changed ? next : current;
    });
  }, [editable, costPricingMultiplier, quotation.type]);

  useEffect(() => {
    // Discover systems from header fields + mapped workingRows; keep display rows empty for lazy load.
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    setQuotation({
      ...baseQuotation,
      systems,
      systemNames: systems.map((system) => system.name),
      systemName: systems.map((system) => system.name).join(", "),
    });
    // System line items load lazily via GET .../get/{id}/{systemName} on system click.
    setWorkingRows(
      systems.length
        ? []
        : ensureSystemWorkingRows(baseQuotation.workingRows, systems, baseQuotation.type)
    );
    setOverheadsPercent(String(baseQuotation.overheadsPct ?? "0"));
    setMarkUpPercent(String(baseQuotation.markUpPct ?? "0"));
    setPreferBackendPricing(hasBackendPricingMetrics(baseQuotation.pricingMetrics));
    setVersions(baseHistory);
    setActionError("");
    setSaveMessage("");
    setRetryExcelFile(null);
    setRetryExcelError("");
    if (retryExcelInputRef.current) retryExcelInputRef.current.value = "";
  }, [baseQuotation, baseHistory]);

  // SERVICE quotations: fill Service Executive / Manager IDs + names from quotation or complaint.
  useEffect(() => {
    const isService = String(quotation.type || "").toUpperCase() === "SERVICE";
    const complaintId = String(quotation.complaintId || "").trim();
    const roles = pickQuotationServiceRoleFields(quotation);
    const missingIds = !roles.technicianId || !roles.managerId;
    const missingNames = !roles.technicianName || !roles.managerName;
    if (!isService || (!missingIds && !missingNames)) return undefined;

    const enrichmentKey = `${quotation.id || ""}|${complaintId}|${roles.technicianId}|${roles.managerId}`;
    if (serviceRoleEnrichmentKeyRef.current === enrichmentKey) return undefined;
    serviceRoleEnrichmentKeyRef.current = enrichmentKey;

    let active = true;
    (async () => {
      try {
        let mergedRoles = { ...roles };

        // Prefer quotation + complaint response mapping for missing IDs/names.
        if (complaintId && (missingIds || missingNames)) {
          const detail = await fetchComplaintDetail(complaintId);
          if (!active || !detail) return;
          const raw = detail?.raw && typeof detail.raw === "object" ? detail.raw : detail;
          mergedRoles = mergeQuotationServiceRoleFields(
            mergedRoles,
            mergeQuotationServiceRoleFields(detail, raw)
          );
        }

        // Resolve display names from employee master when IDs exist but names are blank.
        if (
          (mergedRoles.technicianId && !mergedRoles.technicianName) ||
          (mergedRoles.managerId && !mergedRoles.managerName)
        ) {
          try {
            const employees = await fetchEmployeeOptions();
            if (!active) return;
            if (!mergedRoles.technicianName && mergedRoles.technicianId) {
              const resolved = resolveEmployeeDisplayName(
                mergedRoles.technicianId,
                employees,
                ""
              );
              if (resolved && !/^\d+$/.test(String(resolved).trim())) {
                mergedRoles.technicianName = resolved;
              }
            }
            if (!mergedRoles.managerName && mergedRoles.managerId) {
              const resolved = resolveEmployeeDisplayName(
                mergedRoles.managerId,
                employees,
                ""
              );
              if (resolved && !/^\d+$/.test(String(resolved).trim())) {
                mergedRoles.managerName = resolved;
              }
            }
            setEmployeeOptions(employees);
          } catch {
            // Keep IDs even if employee lookup fails.
          }
        }

        if (
          !mergedRoles.technicianId &&
          !mergedRoles.managerId &&
          !mergedRoles.technicianName &&
          !mergedRoles.managerName
        ) {
          return;
        }

        setQuotation((current) => {
          const next = mergeQuotationServiceRoleFields(current, mergedRoles);
          if (
            next.technicianId === String(current.technicianId || "") &&
            next.managerId === String(current.managerId || "") &&
            next.technicianName === String(current.technicianName || "") &&
            next.managerName === String(current.managerName || "")
          ) {
            return current;
          }
          return hydrateQuotation({ ...current, ...next });
        });
      } catch {
        // Keep quotation as returned by GET.
      }
    })();

    return () => {
      active = false;
    };
  }, [
    quotation.id,
    quotation.type,
    quotation.complaintId,
    quotation.technicianId,
    quotation.managerId,
    quotation.technicianName,
    quotation.managerName,
  ]);

  // Reset lazy tab caches only when the quotation identity changes (not on every history sync).
  useEffect(() => {
    pmcConfigsLoadedRef.current = false;
    termsLibraryLoadedRef.current = false;
    serviceRoleEnrichmentKeyRef.current = "";
    setPmcEntries([]);
    setHasPmcConfigs(false);
    setStandardTerms([]);
    setSelectedStandardTerms([]);
    setAdditionalTermEntries([]);
    setTermsPreviewOrder([]);
  }, [baseQuotation.id, baseQuotation.type]);

  // Prefetch /specific-terms on open so save + Terms rematch do not depend on visiting Terms first.
  useEffect(() => {
    const quotationId = baseQuotation.id;
    if (!quotationId) return undefined;
    let active = true;
    (async () => {
      try {
        const loaded = await loadSpecificTermsForQuotation(
          quotationId,
          baseQuotation.specificTerms || []
        );
        if (!active || !loaded.specificTerms.length) return;
        setQuotation((current) => {
          if (hasQuotationSpecificTermsData(current.specificTerms)) return current;
          return { ...current, specificTerms: loaded.specificTerms };
        });
      } catch {
        // Terms tab lazy-load will retry.
      }
    })();
    return () => {
      active = false;
    };
  }, [baseQuotation.id]);

  useEffect(() => {
    if (activeTab !== "costPricing") return undefined;

    let active = true;

    async function loadPmcForTab() {
      const quotationId = quotation.id || baseQuotation.id;
      const quotationPmcs = quotation.pmcs || baseQuotation.pmcs || [];
      // Show embedded/saved PMCs immediately; refresh from quotation PMC API below.
      if (hasQuotationPmcData(quotationPmcs)) {
        const seeded = seedPmcEntriesFromQuotation(quotationPmcs);
        setHasPmcConfigs(seeded.length > 0);
        setPmcEntries(seeded);
      }
      try {
        const loaded = await loadPmcEntriesForQuotation(quotationId, quotationPmcs);
        if (!active) return;

        if (loaded.source === "quotation" || loaded.source === "embedded") {
          // Keep saved values, but only render cost heads that are Active in PMC configs.
          try {
            const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
              quotation.type || baseQuotation.type,
              loaded.entries
            );
            if (!active) return;
            pmcConfigsLoadedRef.current = true;
            const nextEntries = entries.length
              ? entries
              : loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
            setHasPmcConfigs(hasConfigs || nextEntries.length > 0);
            setPmcEntries(nextEntries);
            setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            return;
          } catch {
            if (!active) return;
            const nextEntries = loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
            pmcConfigsLoadedRef.current = true;
            setHasPmcConfigs(nextEntries.length > 0);
            setPmcEntries(nextEntries);
            setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            return;
          }
        }

        // No saved quotation PMC data — seed configs only on first C+P open.
        if (pmcConfigsLoadedRef.current) return;
        const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
          quotation.type || baseQuotation.type,
          []
        );
        if (!active) return;
        pmcConfigsLoadedRef.current = true;
        setHasPmcConfigs(hasConfigs);
        setPmcEntries(entries);
      } catch {
        if (!active) return;
        if (!pmcConfigsLoadedRef.current) {
          setHasPmcConfigs(hasQuotationPmcData(quotationPmcs));
          setPmcEntries(seedPmcEntriesFromQuotation(quotationPmcs));
          pmcConfigsLoadedRef.current = true;
        }
      }
    }

    loadPmcForTab();
    return () => {
      active = false;
    };
  }, [activeTab, quotation.id, quotation.type, baseQuotation.id, baseQuotation.type]);

  useEffect(() => {
    if (activeTab !== "terms") return undefined;

    let active = true;

    async function loadTermsForTab() {
      if (termsLibraryLoadedRef.current) return;
      const quotationId = quotation.id || baseQuotation.id;
      const quotationTerms = quotation.specificTerms || baseQuotation.specificTerms || [];
      try {
        const state = await loadTermsTabState(
          quotationId,
          quotation.type || baseQuotation.type,
          quotationTerms
        );
        if (!active) return;
        termsLibraryLoadedRef.current = true;
        setStandardTerms(state.standardTerms);
        setSelectedStandardTerms(state.selectedIds);
        setAdditionalTermEntries(state.additionalTermEntries);
        setTermsPreviewOrder(state.previewOrder);
        if (state.specificTerms.length) {
          setQuotation((current) => ({ ...current, specificTerms: state.specificTerms }));
        }
      } catch {
        if (!active) return;
        // Still pull /specific-terms so a library error cannot blank saved selections.
        try {
          const [nextTerms, loaded] = await Promise.all([
            loadStandardTermsForQuotationType(
              quotation.type || baseQuotation.type
            ).catch(() => []),
            loadSpecificTermsForQuotation(quotationId, quotationTerms),
          ]);
          if (!active) return;
          const specificTerms =
            loaded.source === "quotation" || loaded.source === "embedded"
              ? loaded.specificTerms
              : quotationTerms;
          const resolved = resolveTermsFromQuotation(specificTerms, nextTerms || []);
          setStandardTerms(nextTerms || []);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setTermsPreviewOrder(resolved.previewOrder);
          if (specificTerms.length) {
            setQuotation((current) => ({ ...current, specificTerms }));
          }
        } catch {
          if (!active) return;
          const resolved = seedTermsStateFromQuotation(quotationTerms);
          setStandardTerms([]);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setTermsPreviewOrder(resolved.previewOrder);
        }
        termsLibraryLoadedRef.current = true;
      }
    }

    loadTermsForTab();
    return () => {
      active = false;
    };
  }, [activeTab, quotation.id, quotation.type, baseQuotation.id, baseQuotation.type]);

  async function ensureEmployeeOptions() {
    if (employeesRequestedRef.current) return;
    employeesRequestedRef.current = true;
    setLoadingEmployees(true);
    try {
      const employees = await fetchEmployeeOptions();
      setEmployeeOptions(employees);
    } catch {
      employeesRequestedRef.current = false;
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  }

  async function ensureSalesExecutiveOptions() {
    if (salesExecutivesRequestedRef.current) return;
    salesExecutivesRequestedRef.current = true;
    setLoadingSalesExecutives(true);
    try {
      const options = await fetchSalesExecutiveOptions();
      setSalesExecutiveOptions(options);
    } catch {
      salesExecutivesRequestedRef.current = false;
      setSalesExecutiveOptions([]);
    } finally {
      setLoadingSalesExecutives(false);
    }
  }

  async function ensureSalesSupportOptions() {
    if (salesSupportRequestedRef.current) return;
    salesSupportRequestedRef.current = true;
    setLoadingSalesSupport(true);
    try {
      const options = await fetchSalesSupportOptions();
      setSalesSupportOptions(options);
    } catch {
      salesSupportRequestedRef.current = false;
      setSalesSupportOptions([]);
    } finally {
      setLoadingSalesSupport(false);
    }
  }

  // Prefetch role lists in edit so saved Sales Executive / Support IDs preselect correctly.
  useEffect(() => {
    if (isServiceQuotation) return undefined;
    ensureSalesExecutiveOptions();
    ensureSalesSupportOptions();
    return undefined;
  }, [quotation.id, isServiceQuotation]);

  const handleSetOverheadsPercent = useCallback((value) => {
    setPreferBackendPricing(false);
    setOverheadsPercent(value);
  }, []);

  const handleSetMarkUpPercent = useCallback((value) => {
    setPreferBackendPricing(false);
    setMarkUpPercent(value);
  }, []);

  // Stable setter identity is required - WorkingSheetTab catalog effect depends on setRows.
  const handleSetWorkingRows = useCallback((value) => {
    setPreferBackendPricing(false);
    setWorkingRows(value);
  }, []);

  const handleSetPmcEntries = useCallback((value) => {
    setPreferBackendPricing(false);
    setPmcEntries(value);
  }, []);

  /** Mobile Working Sheet: attach an FSM products-services system to the quotation. */
  const handleAddWorkingSheetSystem = useCallback((system) => {
    const nextId = String(system?.id || system?.productServiceId || "").trim();
    const nextName = String(system?.name || "").trim();
    if (!nextId || !nextName) {
      throw new Error("Select a valid system.");
    }
    if (isClientPlaceholderSystemName(nextName) || isClientPlaceholderSystemId(nextId)) {
      throw new Error("Select a valid system from the list.");
    }

    setPreferBackendPricing(false);
    setQuotation((current) => {
      const existing = resolveQuotationSystems(current, workingRows);
      if (
        existing.some(
          (entry) =>
            String(entry.id) === nextId ||
            String(entry.name || "").trim().toLowerCase() === nextName.toLowerCase()
        )
      ) {
        return current;
      }

      const systems = [...existing, { id: nextId, name: nextName }];
      const productServiceIds = systems
        .map((entry) => Number(entry.id))
        .filter((id) => Number.isFinite(id) && id > 0);

      return hydrateQuotation({
        ...current,
        systems,
        productServiceIds,
        systemNames: systems.map((entry) => entry.name),
        systemName: systems.map((entry) => entry.name).join(", "),
      });
    });
  }, [workingRows]);

  const workspaceBackendSheetTotals = quotation.pricingMetrics ? {
    supplyInputAmount: quotation.pricingMetrics.totalInputSupplyAmount,
    inputInstallationAmount: quotation.pricingMetrics.totalInputInstallationAmount,
    supplySubtotal: quotation.pricingMetrics.totalSellingSupplyAmount,
    installationSubtotal: quotation.pricingMetrics.totalSellingInstallationAmount,
  } : null;

  const showDownload = CUSTOMER_STAGE_STATUSES.includes(quotation.status);
  const showSubmit = quotationIsEditable(quotation.status) && isLatestVersion;
  const showApprovedActions = quotation.status === "Approved" && isLatestVersion;
  const showSentActions = quotation.status === "Sent to Customer" && isLatestVersion;
  const showNegotiationActions = quotation.status === "Under Negotiation" && isLatestVersion;
  // Disable Create New Version for a quotation id after it has already created a newer version.
  const showCreateNewVersion =
    isLatestVersion && !versionAlreadyCreatedFromThisId;
  const showRevisionNote = quotation.status === "Draft" && Boolean(quotation.revisionNote);
  const showSubmissionNote = quotation.status === "Pending Approval" && Boolean(quotation.submissionNote);
  const importStatusMessage = String(quotation.importStatusMessage || "").trim();
  const showImportStatusMessage = isImportFailed && Boolean(importStatusMessage);

  const mobileSheetTotals = useMemo(() => {
    const live = computeWorkingTotals(workingRows, quotation.type);
    const preferBackend =
      Boolean(workspaceBackendSheetTotals) &&
      (workingRows.length === 0 || !systemsFullyLoaded || preferBackendPricing);
    if (preferBackend && workspaceBackendSheetTotals) {
      const supplySell = Number(workspaceBackendSheetTotals.supplySubtotal) || 0;
      const installSell = Number(workspaceBackendSheetTotals.installationSubtotal) || 0;
      const supplyCost = Number(workspaceBackendSheetTotals.supplyInputAmount) || 0;
      const installCost = Number(workspaceBackendSheetTotals.inputInstallationAmount) || 0;
      return {
        sellingTotal: supplySell + installSell,
        costTotal: supplyCost + installCost,
        supplyInputAmount: supplyCost,
        inputInstallationAmount: installCost,
        supplySubtotal: supplySell,
        installationSubtotal: installSell,
        itemCount: workingRows.length,
        systemCount: workspaceSystems.length,
      };
    }
    return {
      sellingTotal: live.customerSellingTotal,
      costTotal: live.supplyInputAmount + (supportsInstallation(quotation.type) ? live.inputInstallationAmount : 0),
      supplyInputAmount: live.supplyInputAmount,
      inputInstallationAmount: live.inputInstallationAmount,
      supplySubtotal: live.supplySubtotal,
      installationSubtotal: live.installationSubtotal,
      itemCount: workingRows.length,
      systemCount: workspaceSystems.length,
    };
  }, [
    workingRows,
    quotation.type,
    workspaceBackendSheetTotals,
    systemsFullyLoaded,
    preferBackendPricing,
    workspaceSystems.length,
  ]);

  const mobilePricingSummary = useMemo(() => {
    const live = computeWorkingSheetPricingMetrics({
      rows: workingRows,
      quotationType: quotation.type,
      pmcEntries,
      overheadsPct: overheadsPercent,
      markUpPct: markUpPercent,
    });
    const backend = quotation.pricingMetrics;
    const useBackend =
      preferBackendPricing &&
      hasBackendPricingMetrics(backend) &&
      (workingRows.length === 0 || !systemsFullyLoaded);
    if (useBackend) {
      return {
        grossProfit: backend.grossProfit ?? live.grossProfit,
        grossMarginPct: backend.grossMarginPct ?? live.grossMarginPct,
      };
    }
    return {
      grossProfit: live.grossProfit,
      grossMarginPct: live.grossMarginPct,
    };
  }, [
    workingRows,
    quotation.type,
    quotation.pricingMetrics,
    pmcEntries,
    overheadsPercent,
    markUpPercent,
    preferBackendPricing,
    systemsFullyLoaded,
  ]);

  function goMobileTab(direction) {
    const order = ["workingSheet", "costPricing", "terms", "summary"];
    const index = order.indexOf(activeTab);
    const nextIndex = direction === "next" ? Math.min(order.length - 1, index + 1) : Math.max(0, index - 1);
    setActiveTab(order[nextIndex]);
  }

  function getTermsContext() {
    // Until Terms tab has loaded, keep persisted quotation terms untouched.
    // Caller may await ensurePersistedTermsForSave() first when specificTerms are empty.
    if (!termsLibraryLoadedRef.current) {
      return {
        standardTerms: [],
        selectedStandardTerms: [],
        previewOrder: [],
        specificTerms: Array.isArray(quotation.specificTerms) ? quotation.specificTerms : [],
      };
    }

    const applicableTerms = filterTermsForQuotationType(standardTerms, quotation.type);
    const applicableSelectedIds = selectedStandardTerms.filter((id) =>
      applicableTerms.some((term) => String(term.id) === String(id))
    );
    return {
      standardTerms: applicableTerms,
      selectedStandardTerms: applicableSelectedIds,
      previewOrder: termsPreviewOrder,
      specificTerms: buildSpecificTermsPayload(
        applicableTerms,
        applicableSelectedIds,
        additionalTermEntries,
        termsPreviewOrder
      ),
    };
  }

  /** Until C+P has loaded, keep persisted quotation PMCs untouched on save. */
  function getPmcEntriesForSave() {
    if (!pmcConfigsLoadedRef.current) {
      return Array.isArray(quotation.pmcs) ? quotation.pmcs : [];
    }
    return pmcEntries;
  }

  /** Fetch /pmcs when C+P was never opened so Working Sheet save does not wipe saved PMC rows. */
  async function ensurePersistedPmcsForSave() {
    if (pmcConfigsLoadedRef.current) return getPmcEntriesForSave();
    if (hasQuotationPmcData(quotation.pmcs)) return quotation.pmcs;
    if (!quotation.id) return [];
    try {
      const loaded = await loadPmcEntriesForQuotation(quotation.id, []);
      if (loaded.source === "quotation" || loaded.source === "embedded") {
        setQuotation((current) => ({ ...current, pmcs: loaded.entries }));
        return loaded.entries;
      }
    } catch {
      // Keep empty — nothing persisted to protect.
    }
    return [];
  }

  /** Fetch /specific-terms when Terms was never opened so save does not wipe saved terms. */
  async function ensurePersistedTermsForSave() {
    if (termsLibraryLoadedRef.current) return getTermsContext();
    if (hasQuotationSpecificTermsData(quotation.specificTerms)) return getTermsContext();
    if (!quotation.id) return getTermsContext();
    try {
      const loaded = await loadSpecificTermsForQuotation(quotation.id, []);
      if (loaded.source === "quotation" || loaded.source === "embedded") {
        setQuotation((current) => ({ ...current, specificTerms: loaded.specificTerms }));
        return {
          standardTerms: [],
          selectedStandardTerms: [],
          previewOrder: [],
          specificTerms: loaded.specificTerms,
        };
      }
    } catch {
      // Keep existing context.
    }
    return getTermsContext();
  }

  async function handleBackToComplaint() {
    const complaintId = String(quotation.complaintId || "").trim();
    if (!complaintId) {
      setActionError("Complaint ID is missing for this quotation.");
      return;
    }

    setComplaintBackBusy(true);
    setActionError("");
    try {
      // GET /saas/fsm/customer-complaints/{complaintId} then open that complaint page.
      const detail = await fetchComplaintDetail(complaintId);
      const resolvedComplaintId = String(
        detail?.complaintId ??
        detail?.id ??
        detail?.raw?.complaintId ??
        detail?.raw?.id ??
        complaintId
      ).trim();
      if (!resolvedComplaintId) {
        throw new Error("Complaint ID is missing in the complaint response.");
      }
      navigate(`/fsm/customer-complaints/${encodeURIComponent(resolvedComplaintId)}`, {
        state: detail ? { ticket: detail } : undefined,
      });
    } catch (error) {
      setActionError(error.message || "Failed to open customer complaint.");
    } finally {
      setComplaintBackBusy(false);
    }
  }

  async function saveCustomerDetails() {
    if (!quotation.id) {
      setActionError("Quotation ID is missing. Please refresh and try again.");
      return;
    }

    if (isImportFailed) {
      if (!(retryExcelFile instanceof Blob)) {
        setRetryExcelError(sq("excelUploadRequired"));
        setActionError(sq("excelUploadRequired"));
        return;
      }

      setDetailsSaving(true);
      setActionError("");
      setSaveMessage("");
      setRetryExcelError("");

      try {
        const response = await retryImportQuotation(quotation.id, retryExcelFile);
        const payload =
          extractObjectPayload(response?.data) ||
          extractObjectPayload(response) ||
          null;

        let next = payload ? normalizeApiQuotation(payload) : null;
        if (!next?.refId) {
          const latest = await getQuotationById(quotation.id);
          next = normalizeApiQuotation(latest?.data || {});
        }

        if (!next?.refId) {
          throw new Error("Backend did not return the updated quotation after retry import.");
        }

        await applyLatestQuotationState(next);
        setRetryExcelFile(null);
        if (retryExcelInputRef.current) retryExcelInputRef.current.value = "";
        setSaveMessage(sq("retryImportSuccess"));
        window.setTimeout(() => setSaveMessage(""), 2600);
      } catch (error) {
        setActionError(error.message || sq("retryImportFailed"));
      } finally {
        setDetailsSaving(false);
      }
      return;
    }

    setDetailsSaving(true);
    setActionError("");
    setSaveMessage("");

    try {
      const assignedSalesExecutiveId = resolveAssignedEmployeeId(
        quotation.assignedSalesExecutive,
        quotation.salesExecutive,
        salesExecutiveOptions
      );
      const assignedSalesSupportId = resolveAssignedEmployeeId(
        quotation.assignedSalesSupport,
        quotation.salesSupport,
        salesSupportOptions
      );
      const assignedSalesExecutive = toAssignedEmployeeId(assignedSalesExecutiveId);
      const assignedSalesSupport = toAssignedEmployeeId(assignedSalesSupportId);
      const salesExecutiveName = resolveEmployeeDisplayName(
        assignedSalesExecutiveId,
        salesExecutiveOptions,
        quotation.salesExecutive
      );
      const salesSupportName = resolveEmployeeDisplayName(
        assignedSalesSupportId,
        salesSupportOptions,
        quotation.salesSupport
      );
      const tenderDueDate = String(quotation.tenderDue || "").slice(0, 10);
      const expiryDate = String(quotation.expiry || "").slice(0, 10);
      const productServiceIds = (Array.isArray(quotation.productServiceIds) ? quotation.productServiceIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

      const payload = {
        refId: quotation.refId,
        type: mapQuotationTypeForApi(quotation.type),
        customerId: extractNumericId(quotation.customerId) || quotation.customerId || undefined,
        customerName: quotation.customer || undefined,
        locationId: extractNumericId(quotation.locationId) || quotation.locationId || undefined,
        locationName: quotation.location || undefined,
        address: quotation.location || undefined,
        contactName: quotation.pointOfContact || undefined,
        pointOfContactName: quotation.pointOfContact || undefined,
        contactPhone: quotation.phone || undefined,
        contactEmail: quotation.email || undefined,
        tenderDueDate,
        expiryDate,
        ...(productServiceIds.length
          ? {
              productServiceIds,
              systemNames: quotation.systemNames,
              systemName: quotation.systemName,
            }
          : {}),
        ...(isServiceQuotation
          ? {
              technicianId: toAssignedEmployeeId(quotation.technicianId) || quotation.technicianId || undefined,
              managerId: toAssignedEmployeeId(quotation.managerId) || quotation.managerId || undefined,
              serviceExecutive: quotation.technicianName || undefined,
              serviceManager: quotation.managerName || undefined,
              ...(quotation.complaintId
                ? { complaintId: Number(quotation.complaintId) || quotation.complaintId }
                : {}),
            }
          : {
              assignedSalesExecutive,
              assignedSalesSupport,
            }),
      };

      const response = await updateQuotation(quotation.id, payload);
      const apiData = extractObjectPayload(response?.data) || extractObjectPayload(response) || {};

      setQuotation((current) => hydrateQuotation({
        ...current,
        salesExecutive: isServiceQuotation
          ? current.salesExecutive
          : (apiData.salesExecutive || apiData.assignedSalesExecutiveName || salesExecutiveName),
        salesSupport: isServiceQuotation
          ? current.salesSupport
          : (apiData.salesSupport || apiData.assignedSalesSupportName || salesSupportName),
        assignedSalesExecutive: isServiceQuotation
          ? current.assignedSalesExecutive
          : (apiData.assignedSalesExecutive ?? assignedSalesExecutive ?? current.assignedSalesExecutive),
        assignedSalesSupport: isServiceQuotation
          ? current.assignedSalesSupport
          : (apiData.assignedSalesSupport ?? assignedSalesSupport ?? current.assignedSalesSupport),
        ...mergeQuotationServiceRoleFields(current, apiData),
        tenderDue: apiData.tenderDueDate || tenderDueDate,
        expiry: apiData.expiryDate || expiryDate,
      }));

      setSaveMessage("Customer details saved.");
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to update quotation details.");
    } finally {
      setDetailsSaving(false);
    }
  }

  async function persistWorkingSheet() {
    if (!quotation.id) return quotation;

    // Flush debounced Additional Terms edits and prefer the returned draft snapshot
    // (setState from flush is async and would otherwise miss the latest keystrokes).
    const flushedAdditionalTerms = flushAdditionalTermsRef.current?.() ?? null;

    const systemsForSave = resolveQuotationSystems(quotation, workingRows);
    const loadedRowsForSave = await ensureWorkingRowsLoadedForSave({
      quotationId: quotation.id,
      systems: systemsForSave,
      rows: workingRows,
      quotationType: quotation.type,
      costPricingMultiplier,
      // Mobile refreshes all real systems; desktop only loads systems missing from lazy state.
      // Neither path probes hardcoded placeholder names (Default System / System 1 / …).
      fetchPlaceholderSystems: !isMobileWorkspace,
    });
    const uniqueLoadedRows = dedupeWorkingRowsByLineItemId(
      filterOutClientOnlyBlankWorkingRows(loadedRowsForSave)
    );
    // Sync UI only with real loaded lines — never keep/append client blank placeholders.
    // Mobile: replace with the deduped snapshot (avoid merging a second copy into totals).
    if (isMobileWorkspace) {
      setWorkingRows(uniqueLoadedRows);
    } else if (loadedRowsForSave !== workingRows) {
      const currentIds = new Set(workingRows.map((row) => String(row.id)));
      const addedRows = loadedRowsForSave.filter((row) => !currentIds.has(String(row.id)));
      if (addedRows.length || loadedRowsForSave.length !== workingRows.length) {
        setWorkingRows((current) => {
          const ids = new Set(current.map((row) => String(row.id)));
          const merged = dedupeWorkingRowsByLineItemId([
            ...current.filter((row) => !isClientOnlyBlankWorkingRow(row)),
            ...addedRows.filter((row) => !ids.has(String(row.id))),
          ]);
          return merged.length ? merged : uniqueLoadedRows;
        });
      }
    }

    const rowsForSave = applyNetPriceRuleToRows(uniqueLoadedRows, netPricePercentage).map((row) => {
      const prepared = computePreparedWorkingRow(row, quotation.type, {
        costPricingMultiplier,
      });
      const inputManual = Boolean(row.initialUnitRateManual);
      const sellingManual = Boolean(row.unitRateManual);
      const roundedInputSupplyRate = toExcelWholeRate(
        String(row.initialUnitRate ?? "").trim() !== ""
          ? row.initialUnitRate
          : inputManual
            ? ""
            : prepared.initialUnitRate
      );
      const roundedSellingSupplyRate = toExcelWholeRate(
        String(row.unitRate ?? "").trim() !== ""
          ? row.unitRate
          : sellingManual
            ? ""
            : prepared.unitRate
      );
      return {
        ...row,
        netPrice: prepared.netPrice,
        // Commit Excel-rounded latest edited rates into state used for Update payload + post-save reload.
        initialUnitRate: roundedInputSupplyRate,
        initialUnitRateManual:
          inputManual || String(roundedInputSupplyRate).trim() !== "",
        unitRate: roundedSellingSupplyRate,
        unitRateManual:
          sellingManual || String(roundedSellingSupplyRate).trim() !== "",
        // Persist live C+P Derived Multiplier for non-manual Mult / Install Mult rows.
        adjustmentRate: prepared.adjustmentRate,
        adjustmentRateManual: Boolean(row.adjustmentRateManual),
        multiplier: prepared.multiplier,
        cpMultiplier: prepared.cpMultiplier || costPricingMultiplier || row.cpMultiplier || "",
        installationMultiplier: prepared.installationMultiplier ?? row.installationMultiplier,
        installationMultiplierManual: Boolean(row.installationMultiplierManual),
      };
    });
    const termsContext = await ensurePersistedTermsForSave();
    const pmcEntriesForSave = await ensurePersistedPmcsForSave();
    const cleanedAdditionalTermEntries = (flushedAdditionalTerms ?? additionalTermEntries)
      .map((entry, index) => normalizeAdditionalTermEntry(entry, index))
      .filter((entry) => String(entry.description || "").trim() || String(entry.title || "").trim());
    // Only rebuild from UI selection when Terms tab has loaded. Otherwise keep the
    // persisted specificTerms from ensurePersistedTermsForSave — rebuilding from empty
    // selectedIds would wipe already-saved terms on Working Sheet / Items save.
    const termsContextForSave = {
      ...termsContext,
      specificTerms: termsLibraryLoadedRef.current
        ? buildSpecificTermsPayload(
            termsContext.standardTerms || [],
            termsContext.selectedStandardTerms || [],
            cleanedAdditionalTermEntries,
            termsContext.previewOrder || []
          )
        : Array.isArray(termsContext.specificTerms)
          ? termsContext.specificTerms
          : [],
    };

    const workingSheetPayload = buildWorkingSheetPayload(
      quotation,
      rowsForSave,
      cleanedAdditionalTermEntries,
      pmcEntriesForSave,
      {
        overheadsPct: overheadsPercent,
        markUpPct: markUpPercent,
      },
      termsContextForSave
    );

    await updateQuotationWorkingSheet(
      quotation.id,
      workingSheetPayload
    );

    // Mobile: reload per-system lines so UI/totals use persisted rows with lineItemIds only.
    let mobileRefreshedRows = null;
    if (isMobileWorkspace) {
      mobileRefreshedRows = await reloadMobileWorkingRowsAfterSave({
        quotationId: quotation.id,
        systems: systemsForSave,
        quotationType: quotation.type,
        costPricingMultiplier,
        fallbackRows: rowsForSave,
      });
    }

    // Reload from GET so UI state matches persisted backend values after save.
    try {
      const latestResponse = await getQuotationById(quotation.id);
      const payload =
        unwrapQuotationDetailPayload(latestResponse?.data) ||
        unwrapQuotationDetailPayload(latestResponse) ||
        extractObjectPayload(latestResponse?.data) ||
        extractObjectPayload(latestResponse) ||
        latestResponse?.data ||
        {};
      if (hasQuotationDetailPayload(payload) || extractApiCollection(payload?.lineItems).length > 0) {
        const latestQuotation = normalizeApiQuotation(payload);
        const detailRows =
          Array.isArray(latestQuotation.workingRows) && latestQuotation.workingRows.length
            ? dedupeWorkingRowsByLineItemId(
                filterOutClientOnlyBlankWorkingRows(latestQuotation.workingRows)
              )
            : null;
        // Prefer just-saved PMC/terms when get/{id} omits lazy-loaded collections.
        await applyLatestQuotationState({
          ...latestQuotation,
          workingRows: isMobileWorkspace
            ? (mobileRefreshedRows || detailRows || rowsForSave)
            : (detailRows || rowsForSave),
          pmcs: hasQuotationPmcData(latestQuotation.pmcs)
            ? latestQuotation.pmcs
            : pmcEntriesForSave,
          specificTerms: hasQuotationSpecificTermsData(latestQuotation.specificTerms)
            ? latestQuotation.specificTerms
            : termsContextForSave.specificTerms,
        });
        return latestQuotation;
      }
    } catch {
      // Fall back to the saved local snapshot only if reload fails.
    }

    const nextRows = isMobileWorkspace
      ? (mobileRefreshedRows || rowsForSave)
      : rowsForSave;
    const next = {
      ...quotation,
      workingRows: nextRows,
      pmcs: pmcEntriesForSave,
      specificTerms: termsContextForSave.specificTerms,
      overheadsPct: String(overheadsPercent),
      markUpPct: String(markUpPercent),
    };
    setAdditionalTermEntries(cleanedAdditionalTermEntries);
    setWorkingRows(
      nextRows.map((row) => ({
        ...row,
        preferBackendAmounts: true,
      }))
    );
    setPreferBackendPricing(hasBackendPricingMetrics(quotation.pricingMetrics));
    setQuotation(next);
    if (pmcConfigsLoadedRef.current) {
      setPmcEntries(pmcEntriesForSave);
      setHasPmcConfigs(pmcEntriesForSave.length > 0);
    }
    return next;
  }

  async function saveWorkingSheetDraft() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      await persistWorkingSheet();
      setSaveMessage("Working sheet saved.");
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to save working sheet.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveTermsDraft() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      await persistWorkingSheet();
      setSaveMessage("Terms & conditions saved.");
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to save terms & conditions.");
    } finally {
      setActionBusy(false);
    }
  }

  async function updateSelectedStandardTerm(termId, { title, description }) {
    const existing = standardTerms.find((term) => String(term.id) === String(termId));
    if (!existing) {
      throw new Error("Selected term not found.");
    }

    const numericId = extractNumericId(existing.id);
    if (!numericId) {
      throw new Error("Invalid term id.");
    }

    const nextTitle = String(title || "").trim();
    const nextDescription = String(description || "").trim();
    if (!nextTitle || !nextDescription) {
      throw new Error("Term and description are required.");
    }

    const payload = buildTermApiPayload({
      ...existing,
      title: nextTitle,
      text: nextDescription,
    });

    await updateTerm(numericId, { ...payload, id: numericId });

    const nextStandardTerms = standardTerms.map((term) =>
      String(term.id) === String(termId)
        ? {
            ...term,
            title: nextTitle,
            text: nextDescription,
            description: nextDescription,
          }
        : term
    );
    setStandardTerms(nextStandardTerms);

    // Keep the term selected and sync quotation.specificTerms so refresh remaps to the library term.
    const nextSelectedIds = selectedStandardTerms.some((id) => String(id) === String(termId))
      ? selectedStandardTerms
      : [...selectedStandardTerms, termId];
    setSelectedStandardTerms(nextSelectedIds);

    const applicableTerms = filterTermsForQuotationType(nextStandardTerms, quotation.type);
    const applicableSelectedIds = nextSelectedIds.filter((id) =>
      applicableTerms.some((term) => String(term.id) === String(id))
    );
    const specificTerms = buildSpecificTermsPayload(
      applicableTerms,
      applicableSelectedIds,
      additionalTermEntries,
      termsPreviewOrder
    );

    setQuotation((current) => ({
      ...current,
      specificTerms,
    }));

    if (quotation.id) {
      const systemsForSave = resolveQuotationSystems(quotation, workingRows);
      const loadedRowsForSave = await ensureWorkingRowsLoadedForSave({
        quotationId: quotation.id,
        systems: systemsForSave,
        rows: workingRows,
        quotationType: quotation.type,
        costPricingMultiplier,
        // Desktop: missing real systems only — no placeholder name probes.
        fetchPlaceholderSystems: !isMobileWorkspace,
      });
      const rowsForSave = applyNetPriceRuleToRows(loadedRowsForSave, netPricePercentage);
      const pmcEntriesForSave = await ensurePersistedPmcsForSave();
      await updateQuotationWorkingSheet(
        quotation.id,
        buildWorkingSheetPayload(
          quotation,
          rowsForSave,
          additionalTermEntries,
          pmcEntriesForSave,
          {
            overheadsPct: overheadsPercent,
            markUpPct: markUpPercent,
          },
          {
            standardTerms: applicableTerms,
            selectedStandardTerms: applicableSelectedIds,
            previewOrder: termsPreviewOrder,
            specificTerms,
          }
        )
      );
    }
  }

  async function resolveQuotationId() {
    if (quotation.id) return quotation.id;

    const response = await getQuotations();
    const matched = extractQuotationsList(response)
      .map(normalizeApiQuotation)
      .find((item) => String(item.refId || "").trim().toUpperCase() === String(quotation.refId || "").trim().toUpperCase());

    if (!matched?.id) {
      throw new Error("Quotation was created, but its backend ID could not be resolved yet. Please refresh once and try again.");
    }

    setQuotation((current) => {
      return { ...current, id: matched.id };
    });

    return matched.id;
  }

  async function applyLatestQuotationState(latestQuotation) {
    const latestSystems = resolveQuotationSystems(
      latestQuotation,
      latestQuotation.workingRows || []
    );
    setQuotation((current) => {
      const preservedRoles = mergeQuotationServiceRoleFields(latestQuotation, current);
      return {
        ...latestQuotation,
        ...preservedRoles,
        systems: latestSystems,
        systemNames: latestSystems.map((system) => system.name),
        systemName: latestSystems.map((system) => system.name).join(", "),
      };
    });
    // Keep currently edited/loaded working rows when systems are lazy-loaded.
    // Re-enable backend amount preference so sheet/C+P totals use refreshed GET
    // pricingMetrics until the next local pricing edit.
    if (latestSystems.length) {
      const latestRows = dedupeWorkingRowsByLineItemId(
        filterOutClientOnlyBlankWorkingRows(
          Array.isArray(latestQuotation.workingRows) ? latestQuotation.workingRows : []
        )
      );
      setWorkingRows((current) => {
        // Lazy systems with no local rows yet: stay empty — never adopt invented blanks.
        if (!current.length) return latestRows;
        // Mobile: prefer the post-save snapshot (with lineItemIds) so totals / next save
        // do not keep a second client copy of the same line.
        if (isMobileWorkspace && latestRows.length) {
          return latestRows.map((row) => ({
            ...row,
            preferBackendAmounts: true,
          }));
        }
        return dedupeWorkingRowsByLineItemId(
          current
            .filter((row) => !isClientOnlyBlankWorkingRow(row))
            .map((row) => ({
              ...row,
              preferBackendAmounts: true,
            }))
        );
      });
    } else {
      const latestRows = dedupeWorkingRowsByLineItemId(
        filterOutClientOnlyBlankWorkingRows(
          Array.isArray(latestQuotation.workingRows) ? latestQuotation.workingRows : []
        )
      );
      setWorkingRows((current) => {
        if (latestRows.length) return latestRows;
        const kept = dedupeWorkingRowsByLineItemId(
          current.filter((row) => !isClientOnlyBlankWorkingRow(row))
        );
        if (kept.length) {
          return kept.map((row) => ({
            ...row,
            preferBackendAmounts: true,
          }));
        }
        // Do not invent a blank Working Sheet row after PMC / sheet save.
        return [];
      });
    }

    if (pmcConfigsLoadedRef.current) {
      try {
        if (latestQuotation.id) {
          const loaded = await loadPmcEntriesForQuotation(
            latestQuotation.id,
            latestQuotation.pmcs || []
          );
          if (loaded.source === "quotation" || loaded.source === "embedded") {
            try {
              const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
                latestQuotation.type,
                loaded.entries
              );
              const nextEntries = entries.length
                ? entries
                : loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
              setHasPmcConfigs(hasConfigs || nextEntries.length > 0);
              setPmcEntries(nextEntries);
              setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            } catch {
              const nextEntries = loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
              setHasPmcConfigs(nextEntries.length > 0);
              setPmcEntries(nextEntries);
              setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            }
          }
          // If /pmcs is empty after save, keep the in-memory entries we just persisted.
          // Never overwrite saved C+P values with generic pmc-configs here.
        } else if (hasQuotationPmcData(latestQuotation.pmcs)) {
          const entries = seedPmcEntriesFromQuotation(latestQuotation.pmcs);
          setHasPmcConfigs(entries.length > 0);
          setPmcEntries(entries);
        }
      } catch {
        if (hasQuotationPmcData(latestQuotation.pmcs)) {
          setHasPmcConfigs(true);
          setPmcEntries(seedPmcEntriesFromQuotation(latestQuotation.pmcs || []));
        }
      }
    } else {
      setHasPmcConfigs(false);
      setPmcEntries([]);
    }

    if (termsLibraryLoadedRef.current) {
      try {
        if (latestQuotation.id) {
          const state = await loadTermsTabState(
            latestQuotation.id,
            latestQuotation.type,
            latestQuotation.specificTerms || []
          );
          const remappedEmpty =
            !state.selectedIds.length && !state.additionalTermEntries.length;
          const hasLocalTerms =
            selectedStandardTerms.length > 0 || additionalTermEntries.length > 0;
          // Prefer just-saved / in-memory selection when rematch returns empty
          // (GET often omits specificTerms; /specific-terms can lag after save).
          if (remappedEmpty && hasLocalTerms && !state.specificTerms.length) {
            setStandardTerms(state.standardTerms);
            if (hasQuotationSpecificTermsData(latestQuotation.specificTerms)) {
              setQuotation((current) => ({
                ...current,
                specificTerms: normalizeSpecificTermsCollection(latestQuotation.specificTerms),
              }));
            }
          } else {
            setStandardTerms(state.standardTerms);
            setSelectedStandardTerms(state.selectedIds);
            setAdditionalTermEntries(state.additionalTermEntries);
            setTermsPreviewOrder(state.previewOrder);
            if (state.specificTerms.length) {
              setQuotation((current) => ({
                ...current,
                specificTerms: state.specificTerms,
              }));
            }
          }
        } else if (hasQuotationSpecificTermsData(latestQuotation.specificTerms)) {
          const libraryTerms = standardTerms.length
            ? standardTerms
            : await loadStandardTermsForQuotationType(latestQuotation.type);
          const resolved = resolveTermsFromQuotation(
            latestQuotation.specificTerms || [],
            libraryTerms
          );
          setStandardTerms(libraryTerms);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setTermsPreviewOrder(resolved.previewOrder);
        }
      } catch {
        const libraryTerms = await loadStandardTermsForQuotationType(latestQuotation.type).catch(
          () => []
        );
        const resolved = resolveTermsFromQuotation(
          latestQuotation.specificTerms || [],
          libraryTerms
        );
        setStandardTerms(libraryTerms);
        setSelectedStandardTerms(resolved.selectedIds);
        setAdditionalTermEntries(resolved.additionalTermEntries);
        setTermsPreviewOrder(resolved.previewOrder);
      }
    }
    // If Terms tab has not loaded yet, leave selection state alone — lazy load will hydrate.

    setOverheadsPercent(String(latestQuotation.overheadsPct ?? "0"));
    setMarkUpPercent(String(latestQuotation.markUpPct ?? "0"));
    setPreferBackendPricing(hasBackendPricingMetrics(latestQuotation.pricingMetrics));
  }

  async function submitForApproval() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      const validationMessage = validateWorkingRowsForSubmit(workingRows);
      if (validationMessage) {
        throw new Error(validationMessage);
      }

      await persistWorkingSheet();
      const quotationId = await resolveQuotationId();
      const submitResponse = await submitQuotation(quotationId);
      const submittedPayload =
        extractObjectPayload(submitResponse?.data) ||
        extractObjectPayload(submitResponse) ||
        null;

      let latestQuotation = submittedPayload ? normalizeApiQuotation(submittedPayload) : null;

      if (!latestQuotation?.refId) {
        const latestResponse = await getQuotationById(quotationId);
        if (!hasQuotationDetailPayload(latestResponse?.data) && !hasQuotationDetailPayload(latestResponse)) {
          throw new Error("Backend returned no quotation details for this record.");
        }
        latestQuotation = normalizeApiQuotation(latestResponse?.data || {});
      }

      if (!latestQuotation?.refId) {
        throw new Error("Quotation was submitted, but the updated backend state could not be confirmed.");
      }

      await applyLatestQuotationState(latestQuotation);
      setSaveMessage("");
    } catch (error) {
      setActionError(error.message || "Failed to submit quotation for approval.");
    } finally {
      setActionBusy(false);
    }
  }

  async function createNewVersion() {
    if (creatingVersionRef.current || actionBusy) return;

    if (hasQuotationCreatedVersion(quotation.id)) {
      setShowVersionConfirm(false);
      setActionError("A new version has already been created from this quotation.");
      return;
    }

    if (!isLatestVersion) {
      setActionError("Only the current version can be revised. Open the latest version from Version History.");
      setShowVersionConfirm(false);
      return;
    }

    creatingVersionRef.current = true;
    setActionBusy(true);
    setActionError("");

    try {
      // Persist draft edits first; for locked statuses (e.g. Under Negotiation) skip
      // working-sheet update and go straight to the revise API.
      if (quotationIsEditable(quotation.status) && quotation.id) {
        await persistWorkingSheet();
      }

      if (quotation.id) {
        const sourceQuotationId = quotation.id;
        const response = await reviseQuotation(sourceQuotationId);
        let nextVersion = response?.data && typeof response.data === "object" && !Array.isArray(response.data)
          ? normalizeApiQuotation(response.data)
          : null;

        if (!nextVersion?.refId) {
          const latestListResponse = await getQuotations();
          nextVersion = extractQuotationsList(latestListResponse)
            .map(normalizeApiQuotation)
            .filter((item) => String(item.refId || "").trim().toUpperCase() === String(quotation.refId || "").trim().toUpperCase())
            .sort((a, b) => Number(b.version) - Number(a.version))[0] || null;
        }

        if (!nextVersion?.refId) {
          throw new Error("Backend did not return the revised quotation version.");
        }

        // Lock Create New Version on this source quotation id only.
        markQuotationVersionCreated(sourceQuotationId);
        setShowVersionConfirm(false);

        const nextHistory = buildQuotationVersionHistory(
          [nextVersion, ...versions.map((item) => ({ ...item, superseded: true }))],
          nextVersion.refId || quotation.refId
        );

        navigate(`/sales/quotations/quotation/${nextVersion.id ?? nextVersion.refId}`, {
          state: {
            quotation: nextVersion,
            versionHistory: nextHistory,
          },
        });
        return;
      }
      throw new Error("Quotation revision requires a backend quotation ID. Please refresh and try again.");
    } catch (error) {
      setActionError(error.message || "Failed to create a new revision.");
      setShowVersionConfirm(false);
    } finally {
      creatingVersionRef.current = false;
      setActionBusy(false);
    }
  }

  async function applyCustomerStageStatus({ status, note = "", successMessage }) {
    if (!quotation.id) {
      setActionError("This quotation does not have a backend ID yet, so status update is unavailable.");
      return;
    }

    const apiStatus = mapQuotationStatusForApi(status);
    if (!apiStatus) {
      setActionError("Unable to determine the next quotation status for this action.");
      return;
    }

    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      const statusResponse = await updateQuotationStatus(quotation.id, {
        status: apiStatus,
        note: String(note || statusNote || "").trim(),
      });
      const statusPayload =
        extractObjectPayload(statusResponse?.data) ||
        extractObjectPayload(statusResponse) ||
        null;

      let next = statusPayload ? normalizeApiQuotation(statusPayload) : null;
      if (!next?.refId) {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation(latest?.data || {});
      }

      if (!next?.refId) {
        throw new Error("Backend did not return the updated quotation after status change.");
      }

      // Always reflect the status returned by the backend after a successful update.
      await applyLatestQuotationState(next);
      setStatusNote("");
      setSaveMessage(
        successMessage || `Quotation status updated to ${next.status || apiStatus}.`
      );
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to update quotation status.");
    } finally {
      setActionBusy(false);
    }
  }

  async function markAsSentToCustomer() {
    await applyCustomerStageStatus({
      status: "Sent to Customer",
      successMessage: "Quotation marked as Sent to Customer.",
    });
  }

  async function markUnderNegotiation() {
    await applyCustomerStageStatus({
      status: "Under Negotiation",
      successMessage: "Quotation marked as Under Negotiation.",
    });
  }

  async function markAccepted() {
    await applyCustomerStageStatus({
      status: "Accepted",
      successMessage: "Quotation marked as Accepted.",
    });
  }

  async function markRejected() {
    await applyCustomerStageStatus({
      status: "Rejected",
      successMessage: "Quotation marked as Rejected.",
    });
  }

  async function handleDownloadPdf() {
    const quotationId = quotation.id ?? baseQuotation.id;
    if (quotationId == null || String(quotationId).trim() === "") {
      setActionError("This quotation does not have a backend ID yet, so PDF download is unavailable.");
      return;
    }

    setActionBusy(true);
    setActionError("");

    try {
      // Clear leftover placeholder System names (e.g. "Default System") before PDF render.
      if (editable && quotationIsEditable(quotation.status) && quotation.id) {
        await persistWorkingSheet();
      }

      const result = await downloadQuotationPdf(quotationId);
      if (!(result?.blob instanceof Blob) || result.blob.size === 0) {
        throw new Error("Failed to download quotation PDF.");
      }

      const fallbackName = `${quotation.refId || baseQuotation.refId || "quotation"}.pdf`;
      const filename = parseContentDispositionFilename(result.filename, fallbackName);
      triggerBrowserDownload(
        result.blob,
        /\.pdf$/i.test(filename) ? filename : `${filename}.pdf`
      );
    } catch (error) {
      setActionError(error.message || "Failed to download quotation PDF.");
    } finally {
      setActionBusy(false);
    }
  }

  const mobileServiceExecutiveLabel = resolveEmployeeDisplayName(
    quotation.technicianId,
    employeeOptions,
    quotation.technicianName
  );
  const mobileServiceManagerLabel = resolveEmployeeDisplayName(
    quotation.managerId,
    employeeOptions,
    quotation.managerName
  );

  return (
    <div className={isMobileWorkspace ? "flex min-h-0 flex-1 flex-col" : "space-y-3"}>
      <div
        className={
          isMobileWorkspace
            ? "min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain"
            : "contents"
        }
      >
      {normalizeStatusLabel(quotation.status) === "In Progress" && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
        >
          <Clock3 aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Your quotation is being processed</p>
            <p className="mt-1">
              This page will automatically refresh every 10 seconds to check the latest
              quotation status. Updates will appear here once processing is complete.
            </p>
          </div>
        </div>
      )}
      {isMobileWorkspace ? (
        <div className="space-y-2">
          <QuotationMobileHeader
            refId={quotation.refId}
            onBack={() => navigate("/sales/quotations/quotation")}
            onOpenMenu={() => setMobileMenuOpen(true)}
            status={translateQuotationStatus(quotation.status)}
            versionLabel={quotation.versionLabel}
            typeLabel={translateQuotationType(quotation.type)}
            showSave={
              editable &&
              (activeTab === "workingSheet" ||
                activeTab === "costPricing" ||
                activeTab === "terms")
            }
            saveBusy={actionBusy}
            onSave={activeTab === "terms" ? saveTermsDraft : saveWorkingSheetDraft}
          />
          {!isLatestVersion && (
            <div className="rounded-[8px] bg-[#f7faff] px-2.5 py-1.5 text-[11px] leading-4 text-[#5d7598]">
              Viewing a previous version (read-only). Only the current version
              {latestVersionNo != null ? ` (V${latestVersionNo})` : ""} can be edited.
            </div>
          )}
          {showSubmissionNote && (
            <div className="rounded-[8px] bg-[#f4fff8] px-2.5 py-1.5 text-[11px] leading-4 text-[#2b7a4b]">
              <p className="mt-0">{quotation.submissionNote}</p>
            </div>
          )}
          {showRevisionNote && (
            <div className="rounded-[8px] bg-[#fff8ed] px-2.5 py-1.5 text-[11px] leading-4 text-[#b06c16]">
              <p className="font-semibold">Returned for revision</p>
              <p className="mt-0.5">{quotation.revisionNote}</p>
            </div>
          )}
          {showImportStatusMessage && (
            <div className="rounded-[8px] bg-[#fff6f5] px-2.5 py-1.5 text-[11px] leading-4 text-[#b04d43]">
              <p className="m-0 break-words whitespace-pre-wrap [overflow-wrap:anywhere]">
                {importStatusMessage}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
      <PageTitleRow className="mb-5" back={<PageBackLink to="/sales/quotations/quotation" />}><h1><Trans i18nKey="salesQuotation.quotationDetails">Quotation details</Trans></h1></PageTitleRow>
      <div className="rounded-[12px] border border-[#d8e0ed] bg-white px-4 py-3 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] sm:px-5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 w-full flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[20px] font-semibold tracking-[0.01em] text-[#23324c]">{quotation.refId}</h2>
              <StatusPill tone={quotation.statusTone} label={quotation.status} />
              <span className="rounded-full bg-[#eef2f8] px-2.5 py-0.5 text-[11px] font-semibold text-[#647b9f]">{quotation.versionLabel}</span>
              {quotation.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
              {!isLatestVersion && <StatusPill tone="superseded" label="Previous version" />}
            </div>
            <p className="max-w-4xl text-[13px] leading-5 text-[#7384a2]">
              {quotation.customer} · {translateQuotationType(quotation.type)} · {quotation.location}
            </p>
            {!isLatestVersion && (
              <div className="max-w-3xl rounded-[10px] border border-[#d8e0ed] bg-[#f7faff] px-3 py-2 text-[12px] leading-5 text-[#5d7598]">
                Viewing a previous version (read-only). Only the current version
                {latestVersionNo != null ? ` (V${latestVersionNo})` : ""} can be edited.
              </div>
            )}
            {showSubmissionNote && (
              <div className="max-w-3xl rounded-[10px] border border-[#d5e8dc] bg-[#f4fff8] px-3 py-2 text-[12px] leading-5 text-[#2b7a4b]">
                <p className="mt-0">{quotation.submissionNote}</p>
              </div>
            )}
            {showRevisionNote && (
              <div className="max-w-3xl rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2 text-[12px] leading-5 text-[#b06c16]">
                <p className="font-semibold">Returned for revision</p>
                <p className="mt-0.5">{quotation.revisionNote}</p>
              </div>
            )}
            {showImportStatusMessage && (
              <div className="box-border w-full min-w-0 max-w-full rounded-[10px] border border-[#f4cfcb] bg-[#fff6f5] px-3 py-2 text-[12px] leading-5 text-[#b04d43] sm:max-w-3xl">
                <p className="m-0 max-w-full break-words whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {importStatusMessage}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 lg:shrink-0">
            {showSubmit && (
              <QuotationPrimaryButton icon={Send} onClick={submitForApproval} disabled={actionBusy}>
                {actionBusy ? sq("submitting") : sq("submitForApproval")}
              </QuotationPrimaryButton>
            )}
            {showCreateNewVersion && (
              <QuotationSecondaryButton
                icon={Sparkles}
                onClick={() => setShowVersionConfirm(true)}
                disabled={actionBusy || versionAlreadyCreatedFromThisId}
              >
                {sq("createNewVersion")}
              </QuotationSecondaryButton>
            )}
            <QuotationSecondaryButton icon={Clock3} onClick={() => setHistoryOpen(true)}>
              {sq("versionHistory")}
            </QuotationSecondaryButton>
            {showDownload && (
              <QuotationSecondaryButton icon={FileText} onClick={handleDownloadPdf} disabled={actionBusy}>
                {sq("downloadPdf")}
              </QuotationSecondaryButton>
            )}
          </div>
        </div>

        {(showApprovedActions || showSentActions || showNegotiationActions) && (
          <div className="mt-2.5 rounded-[10px] border border-[#e1e7f2] bg-[#fbfcff] px-3 py-2.5">
            <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap gap-2">
                {showApprovedActions && (
                  <QuotationPrimaryButton icon={Send} onClick={markAsSentToCustomer} disabled={actionBusy}>
                    Mark as Sent to Customer
                  </QuotationPrimaryButton>
                )}
                {showSentActions && (
                  <>
                    <QuotationSecondaryButton icon={Clock3} onClick={markUnderNegotiation} disabled={actionBusy}>
                      Mark Under Negotiation
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                      Mark Accepted
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                      Mark Rejected
                    </QuotationSecondaryButton>
                  </>
                )}
                {showNegotiationActions && (
                  <>
                    <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                      Mark Accepted
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                      Mark Rejected
                    </QuotationSecondaryButton>
                  </>
                )}
              </div>
              <div className="flex min-w-[240px] items-center gap-2">
                <input
                  value={statusNote}
                  onChange={(event) => setStatusNote(event.target.value)}
                  placeholder="Optional note for this status update"
                  className="min-h-[36px] w-full rounded-[10px] border border-[#d1d9e8] bg-white px-3 text-[12px] text-[#2d394c] outline-hidden placeholder:text-[#96a5bb]"
                />
              </div>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {actionError && <div className="mt-1"><ErrorState message={actionError} onRetry={() => setActionError("")} /></div>}
      {saveMessage ? (
        <div className="rounded-[10px] border border-[#d5e8dc] bg-[#f4fff8] px-3 py-2 text-[#2b7a4b]">
          <p className="text-[12px] font-semibold">{saveMessage}</p>
        </div>
      ) : null}

      {isMobileWorkspace && (showApprovedActions || showSentActions || showNegotiationActions) ? (
        <div className="rounded-[10px] border border-[#e1e7f2] bg-[#fbfcff] px-3 py-2.5">
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap gap-2">
              {showApprovedActions && (
                <QuotationPrimaryButton icon={Send} onClick={markAsSentToCustomer} disabled={actionBusy}>
                  Mark as Sent to Customer
                </QuotationPrimaryButton>
              )}
              {showSentActions && (
                <>
                  <QuotationSecondaryButton icon={Clock3} onClick={markUnderNegotiation} disabled={actionBusy}>
                    Mark Under Negotiation
                  </QuotationSecondaryButton>
                  <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                    Mark Accepted
                  </QuotationSecondaryButton>
                  <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                    Mark Rejected
                  </QuotationSecondaryButton>
                </>
              )}
              {showNegotiationActions && (
                <>
                  <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                    Mark Accepted
                  </QuotationSecondaryButton>
                  <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                    Mark Rejected
                  </QuotationSecondaryButton>
                </>
              )}
            </div>
            <input
              value={statusNote}
              onChange={(event) => setStatusNote(event.target.value)}
              placeholder="Optional note for this status update"
              className="min-h-[40px] w-full rounded-[10px] border border-[#d1d9e8] bg-white px-3 text-[12px] text-[#2d394c] outline-hidden placeholder:text-[#96a5bb]"
            />
          </div>
        </div>
      ) : null}

      {!isMobileWorkspace ? (
      <SectionShell
        compact
        title={sq("customerAndDetails")}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {isServiceQuotation ? (
              <button
                type="button"
                onClick={handleBackToComplaint}
                disabled={
                  complaintBackBusy ||
                  detailsSaving ||
                  !String(quotation.complaintId || "").trim()
                }
                className="page-back-link disabled:cursor-not-allowed disabled:opacity-60"
                title="Go to Customer Complaint"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <span>{complaintBackBusy ? "Loading..." : `Go to Customer Complaint: ${quotation.complaintId}`}</span>
              </button>
            ) : null}
            {canSaveCustomerDetails ? (
              <button
                type="button"
                onClick={saveCustomerDetails}
                disabled={
                  detailsSaving ||
                  complaintBackBusy ||
                  !quotation.id ||
                  (isImportFailed && !(retryExcelFile instanceof Blob))
                }
                className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:text-[#8da0be]"
              >
                <Save className="h-3.5 w-3.5" />
                {detailsSaving ? "Saving..." : "Save"}
              </button>
            ) : (
              <div className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#9baac0]">
                <Lock className="h-3.5 w-3.5" />
                <span>{sq("locked")}</span>
              </div>
            )}
          </div>
        }
      >
        <div className="grid gap-3 px-4 py-3.5 sm:grid-cols-2 sm:gap-x-5 sm:px-5 lg:grid-cols-3">
          {[
            { key: "customer", label: sq("customerName"), type: "text", viewOnly: true },
            { key: "location", label: sq("location"), type: "text", viewOnly: true },
            { key: "pointOfContact", label: sq("pointOfContact"), type: "text", viewOnly: true },
            { key: "phone", label: sq("phoneNumber"), type: "tel", viewOnly: true },
            { key: "email", label: sq("email"), type: "email", viewOnly: true },
            ...(isServiceQuotation
              ? [
                  { key: "technicianName", label: sq("serviceExecutive"), type: "text", viewOnly: true, serviceRole: true },
                  { key: "managerName", label: sq("serviceManager"), type: "text", viewOnly: true, serviceRole: true },
                ]
              : [
                  { key: "assignedSalesExecutive", label: sq("salesExecutive"), type: "select", employeeField: "salesExecutive" },
                  { key: "assignedSalesSupport", label: sq("salesSupport"), type: "select", employeeField: "salesSupport" },
                ]),
            { key: "tenderDue", label: sq("tenderDue"), type: "date" },
            { key: "expiry", label: sq("expiry"), type: "date" },
          ].map((field) => {
            const roleOptions = field.employeeField === "salesSupport"
              ? salesSupportOptions
              : salesExecutiveOptions;
            const roleLoading = field.employeeField === "salesSupport"
              ? loadingSalesSupport
              : loadingSalesExecutives;
            const ensureRoleOptions = field.employeeField === "salesSupport"
              ? ensureSalesSupportOptions
              : ensureSalesExecutiveOptions;
            const assignedId = field.type === "select"
              ? resolveAssignedEmployeeId(quotation[field.key], quotation[field.employeeField], roleOptions)
              : "";
            const serviceRoleValue = field.serviceRole
              ? resolveEmployeeDisplayName(
                  field.key === "technicianName" ? quotation.technicianId : quotation.managerId,
                  employeeOptions,
                  quotation[field.key]
                )
              : "";

            return (
            <div key={field.key}>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">{field.label}</p>
              {field.type === "select" ? (
                <FormSelect
                  value={assignedId}
                  disabled={!editable || detailsSaving}
                  onPointerDown={ensureRoleOptions}
                  onFocus={ensureRoleOptions}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    const nextName = resolveEmployeeDisplayName(nextId, roleOptions);
                    setQuotation((current) => hydrateQuotation({
                      ...current,
                      [field.key]: nextId,
                      [field.employeeField]: nextName,
                    }));
                  }}
                  className={`${filterControlClass()} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                  {...localizedFieldProps()}
                >
                  <option value="">
                    {roleLoading && roleOptions.length === 0
                      ? "Loading..."
                      : field.employeeField === "salesSupport"
                        ? sq("selectSalesSupport")
                        : sq("selectSalesExecutive")}
                  </option>
                  {assignedId &&
                    !roleOptions.some((option) => String(option.id) === String(assignedId)) && (
                      <option value={assignedId}>
                        {quotation[field.employeeField] || assignedId}
                      </option>
                    )}
                  {roleOptions.map((option) => (
                    <option key={`${field.key}-${option.id}`} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </FormSelect>
              ) : (
                <input
                  type={field.type}
                  value={
                    field.serviceRole
                      ? serviceRoleValue
                      : field.type === "date"
                        ? String(quotation[field.key] || "").slice(0, 10)
                        : (quotation[field.key] || "")
                  }
                  disabled={field.viewOnly || !editable || detailsSaving}
                  readOnly={Boolean(field.viewOnly)}
                  onChange={(event) => {
                    if (field.viewOnly) return;
                    const value = event.target.value;
                    setQuotation((current) => hydrateQuotation({ ...current, [field.key]: value }));
                  }}
                  className={`${filterControlClass()} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                  {...localizedFieldProps()}
                />
              )}
            </div>
            );
          })}

          {isImportFailed && isLatestVersion ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">
                {sq("excelUpload")}
              </p>
              <input
                ref={retryExcelInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  const name = String(file?.name || "").toLowerCase();
                  if (file && !(name.endsWith(".xlsx") || name.endsWith(".xls"))) {
                    setRetryExcelFile(null);
                    setRetryExcelError(sq("excelUploadInvalidType"));
                    event.target.value = "";
                    return;
                  }
                  setRetryExcelFile(file);
                  setRetryExcelError("");
                  setActionError("");
                }}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!detailsSaving) retryExcelInputRef.current?.click();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (!detailsSaving) retryExcelInputRef.current?.click();
                  }
                }}
                className={`flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-[12px] border border-dashed bg-[#fafcff] px-4 py-5 text-center transition hover:border-[#95addd] hover:bg-[#f5f8fd] ${
                  retryExcelError ? "border-[#e4a5a0]" : "border-[#d1d9e8]"
                } ${detailsSaving ? "pointer-events-none opacity-70" : ""}`}
              >
                <div className="grid h-11 w-11 place-items-center rounded-[10px] border border-[#dde5f1] bg-white text-[#2944a8] shadow-[0_8px_18px_-16px_rgba(30,51,131,0.35)]">
                  <FileText className="h-5 w-5" />
                </div>
                {retryExcelFile ? (
                  <div className="flex max-w-full flex-col items-center gap-2">
                    <p className="max-w-full truncate text-[13px] font-semibold text-[#2d394c]">
                      {retryExcelFile.name}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          retryExcelInputRef.current?.click();
                        }}
                        className="inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff]"
                      >
                        {sq("browse")}
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRetryExcelFile(null);
                          setRetryExcelError("");
                          if (retryExcelInputRef.current) retryExcelInputRef.current.value = "";
                        }}
                        className="inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#6f82a1] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] hover:text-[#2944a8]"
                      >
                        {sq("removeFile")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <p className="text-[13px] font-semibold text-[#2d394c]">{sq("noFileSelected")}</p>
                    <p className="text-[12px] text-[#8ea0bd]">{sq("retryImportHint")}</p>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        retryExcelInputRef.current?.click();
                      }}
                      className="mt-1 inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff]"
                    >
                      {sq("browse")}
                    </button>
                  </div>
                )}
              </div>
              {retryExcelError ? (
                <p className="mt-1.5 text-[11px] font-medium text-[#c74b40]">{retryExcelError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </SectionShell>
      ) : null}

      <div className={`space-y-3 ${isMobileWorkspace ? "space-y-2.5" : ""}`}>
      <div className={`flex items-end justify-between gap-3 border-b border-[#d7dfec] px-1 pt-3 ${isMobileWorkspace ? "flex-col items-stretch gap-0 border-0 px-0 pt-0" : ""}`}>
          {isMobileWorkspace ? (
            <>
              <div className="-mx-1 flex border-b border-[#e1e7f2] bg-white">
                {[
                  { key: "workingSheet", label: sq("mobileTabItems") },
                  { key: "costPricing", label: sq("mobileTabPricing") },
                  { key: "terms", label: sq("mobileTabTerms") },
                  { key: "summary", label: sq("mobileTabSummary") },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`min-h-[42px] flex-1 px-1 text-[12px] font-bold transition ${
                      activeTab === tab.key
                        ? "border-b-[3px] border-[#243c93] text-[#243c93]"
                        : "border-b-[3px] border-transparent text-[#8ea0bd]"
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
          <>
          <div className="flex flex-wrap items-end gap-6">
          {[
            { key: "workingSheet", label: sq("workingSheet") },
            { key: "costPricing", label: sq("costPricing"), badge: sq("internal") },
            { key: "terms", label: sq("termsConditions") },
          ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`relative pb-3.5 text-[14px] font-semibold leading-5 transition ${activeTab === tab.key ? "text-[#2944a8]" : "text-[#6f82a1]"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span>{tab.label}</span>
            {tab.badge && <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#ef8c16]">{tab.badge}</span>}
            {activeTab === tab.key && <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[#2944a8]" />}
          </button>
        ))}
          </div>
          {editable ? (
            <button
              type="button"
              onClick={activeTab === "terms" ? saveTermsDraft : saveWorkingSheetDraft}
              disabled={actionBusy}
              className="mb-2 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[8px] border border-[#d0d9ea] bg-white px-3.5 text-[13px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:text-[#8da0be]"
            >
              <Save className="h-4 w-4" />
              {actionBusy ? "Saving..." : "Save"}
            </button>
          ) : null}
          </>
          )}
      </div>

      {/* Keep Working Sheet mounted while on other tabs so expanded/collapsed Systems persist. */}
      <div className={activeTab === "workingSheet" ? "block" : "hidden"} aria-hidden={activeTab !== "workingSheet"}>
        {isMobileWorkspace ? (
          <div className="mb-2">
            <QuotationMobileCustomerCard
              open={mobileCustomerOpen}
              onToggle={() => setMobileCustomerOpen((current) => !current)}
              quotation={quotation}
              editable={editable}
              detailsSaving={detailsSaving}
              canSave={canSaveCustomerDetails}
              isServiceQuotation={isServiceQuotation}
              serviceExecutiveLabel={mobileServiceExecutiveLabel}
              serviceManagerLabel={mobileServiceManagerLabel}
              onChangeField={(key, value) => {
                setQuotation((current) => hydrateQuotation({ ...current, [key]: value }));
              }}
              onSave={saveCustomerDetails}
            />
          </div>
        ) : null}
        <WorkingSheetTab
          quotationId={quotation.id}
          quotationType={quotation.type}
          rows={workingRows}
          setRows={handleSetWorkingRows}
          editable={editable}
          saving={actionBusy}
          systems={workspaceSystems}
          costPricingMultiplier={costPricingMultiplier}
          backendSheetTotals={workspaceBackendSheetTotals}
          preferBackendTotals={preferBackendPricing || !systemsFullyLoaded}
          onAddSystem={handleAddWorkingSheetSystem}
        />
      </div>
      {activeTab === "costPricing" ? (
        <CostPricingTab
          quotationType={quotation.type}
          rows={workingRows}
          setRows={setWorkingRows}
          editable={editable}
          pmcEntries={pmcEntries}
          setPmcEntries={handleSetPmcEntries}
          hasPmcConfigs={hasPmcConfigs}
          overheadsPercent={overheadsPercent}
          setOverheadsPercent={handleSetOverheadsPercent}
          markUpPercent={markUpPercent}
          setMarkUpPercent={handleSetMarkUpPercent}
          netPricePercentage={netPricePercentage}
          backendPricing={quotation.pricingMetrics}
          preferBackendPricing={preferBackendPricing}
          forceBackendSheetCosts={!systemsFullyLoaded}
        />
      ) : null}
      {activeTab === "terms" && (
        <TermsAndConditionsTab
          standardTerms={standardTerms}
          selectedStandardTerms={selectedStandardTerms}
          setSelectedStandardTerms={setSelectedStandardTerms}
          additionalTermEntries={additionalTermEntries}
          setAdditionalTermEntries={setAdditionalTermEntries}
          previewOrder={termsPreviewOrder}
          setPreviewOrder={setTermsPreviewOrder}
          editable={editable}
          onSave={editable ? saveTermsDraft : undefined}
          saving={actionBusy}
          quotationType={quotation.type}
          onUpdateStandardTerm={editable ? updateSelectedStandardTerm : undefined}
          flushAdditionalTermsRef={flushAdditionalTermsRef}
        />
      )}
      {isMobileWorkspace && activeTab === "summary" ? (
        <QuotationMobileSummaryPanel
          formatMoney={formatCurrency}
          sellingTotal={mobileSheetTotals.sellingTotal}
          costTotal={mobileSheetTotals.costTotal}
          systemsCount={mobileSheetTotals.systemCount}
          itemsCount={mobileSheetTotals.itemCount}
          supplyInputAmount={mobileSheetTotals.supplyInputAmount}
          inputInstallationAmount={mobileSheetTotals.inputInstallationAmount}
          supplySubtotal={mobileSheetTotals.supplySubtotal}
          installationSubtotal={mobileSheetTotals.installationSubtotal}
          showInstallation={supportsInstallation(quotation.type)}
          grossProfit={mobilePricingSummary.grossProfit}
          grossMarginPct={mobilePricingSummary.grossMarginPct}
        />
      ) : null}
      </div>
      </div>

      {isMobileWorkspace ? (
        <div className="shrink-0 pt-3">
          <QuotationMobileFooterBar
            activeTab={activeTab}
            showSubmit={showSubmit}
            actionBusy={actionBusy}
            submittingLabel={sq("submitting")}
            submitLabel={sq("submitForApproval")}
            waitingLabel={sq("mobileWaitingApproval")}
            onBackTab={() => goMobileTab("back")}
            onNextTab={() => goMobileTab("next")}
            onSubmit={submitForApproval}
          />
        </div>
      ) : null}

      {isMobileWorkspace ? (
        <QuotationMobileMenuSheet
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          showCreateNewVersion={showCreateNewVersion}
          createVersionDisabled={versionAlreadyCreatedFromThisId}
          onCreateNewVersion={() => setShowVersionConfirm(true)}
          onVersionHistory={() => setHistoryOpen(true)}
          showDownload={showDownload}
          onDownloadPdf={handleDownloadPdf}
          isServiceQuotation={isServiceQuotation}
          complaintId={String(quotation.complaintId || "").trim()}
          complaintBackBusy={complaintBackBusy}
          onBackToComplaint={handleBackToComplaint}
          actionBusy={actionBusy}
        />
      ) : null}

      <VersionHistoryDrawer
        open={historyOpen}
        versions={versions}
        latestVersionNo={latestVersionNo}
        viewingVersionNo={resolveBackendVersionNo(quotation)}
        viewingId={quotation.id}
        loading={historyLoading}
        error={historyError}
        onClose={() => setHistoryOpen(false)}
        onSelectVersion={(version) => {
          setHistoryOpen(false);
          navigate(`/sales/quotations/quotation/${version.id ?? version.refId}`, {
            state: {
              quotation: version,
              versionHistory: versions,
            },
          });
        }}
      />

      <QuotationImportFailureDialog
        open={isImportFailed && importFailureOpen}
        reason={importStatusMessage}
        onOpenChange={setImportFailureOpen}
        onCreateAgain={() => navigate("/sales/quotations/quotation")}
      />
      <QuotationConfirmModal
        open={showVersionConfirm}
        title="Create new version?"
        description="Create a new version from the latest quotation? Existing draft edits will be saved first when the quotation is editable."
        confirmLabel={actionBusy ? "Creating..." : "Create New Version"}
        cancelLabel="Cancel"
        confirming={actionBusy}
        tone="primary"
        onConfirm={createNewVersion}
        onCancel={() => {
          if (actionBusy) return;
          setShowVersionConfirm(false);
        }}
      />
    </div>
  );
}

export {
  QuotationWorkspace
};
